import type { GuideWalkthroughDef } from "../types";
import { TRANSPORT_LIST_ROUTE } from "./steps/shared";

/**
 * Batch editing, as two in-order concepts: select several legs → learn where
 * the one bulk change lives. It runs INSIDE an order (batch-editing legs is an
 * in-order action) and only an editable one — `requires` gates it on the
 * `recap-focus-editable` marker (PENDING / AI_PROPOSAL), so it never starts on
 * the list or a confirmed order whose lines are locked.
 */
export const batchEditLines: GuideWalkthroughDef = {
  id: "batch-edit-lines",
  titleKey: "guide.walkthroughs.batchEditLines.title",
  requires: "recap-focus-editable",
  requiresBodyKey: "guide.requiresEditableOrder",
  steps: [
    {
      // ACTION: select several legs. Spotlight the whole selection (checkbox)
      // COLUMN across every row — `flow-select-column` is an invisible band
      // standing in for it (the per-row checkbox is hover-revealed and
      // templated, so it can't be spotlit) — and let the user tick any row
      // without dismissing (`safeZone`). Advance only once TWO legs are
      // selected — `flow-batch-2plus` mounts then — not on the first.
      route: TRANSPORT_LIST_ROUTE,
      target: "flow-select-column",
      safeZone: "recap-flows-table",
      bodyKey: "guide.walkthroughs.batchEditLines.selectBody",
      advanceOn: "appear",
      appearTarget: "flow-batch-2plus",
    },
    {
      // EXPLAIN: encircle the WHOLE batch toolbar and name what it does — set
      // ONE change (dates / sender / recipient) across every selected leg. The
      // user reads, optionally sets a value, then clicks Suivant. (Not gated on
      // a staged change: `transport-batch-validate` may already be present from
      // an earlier edit, which would instantly skip this step.) `safeZone` keeps
      // the guide alive while they open the pickers.
      route: TRANSPORT_LIST_ROUTE,
      target: "flow-batch-island",
      safeZone: "recap-flows-table",
      bodyKey: "guide.walkthroughs.batchEditLines.changeBody",
      advanceOn: "next",
      // The batch toolbar sits bottom-center, where the advance button would
      // land — move the button up so it doesn't cover the thing it describes.
      advanceButtonPos: "top-center",
    },
    {
      // EXPLAIN (final): spotlight the Valider button — it applies the staged
      // change to every selected leg. Clicking it commits and ends the tutorial
      // (the button unmounts once the buffer flushes). It only mounts while a
      // change is staged (`pendingChangeCount > 0`); if the user hasn't set one
      // by now, the step fails fast (no marker to point at) rather than lingering.
      route: TRANSPORT_LIST_ROUTE,
      target: "transport-batch-validate",
      safeZone: "recap-flows-table",
      bodyKey: "guide.walkthroughs.batchEditLines.validateBody",
      advanceOn: "click-target",
      advanceButtonPos: "top-center",
    },
  ],
};
